import { DecreeSingleSelect } from "@/components/form-control/DecreeSingleSelect";
import { DaysOfWeek, DecreeCategory } from "@/types/gql/graphql";
import * as z from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { isNil } from "helper/utils";
import { useEffect, useMemo } from "react";
import { RestrictionModalProps } from "types/restriction.type";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputNumber } from "components/form-control/input-number";
import { TimeSpanListing } from "components/time-span";
import { getPlace } from "helper/format-data";
import { useForm } from "react-hook-form";

const restrictionSchema = z.object({
  timeSpans: z
    .array(
      z.object({
        startDate: z.date({ message: "La date de début est requise" }),
        endDate: z.date({ message: "La date de fin est requise" }),
        daysOfWeek: z.array(z.enum(DaysOfWeek)).optional().nullable(),
        daysOfMonth: z.array(z.number()).optional().nullable(),
        months: z.array(z.number()).optional().nullable(),
        timeOfDayStart: z.string().optional().nullable(),
        timeOfDayEnd: z.string().optional().nullable(),
      }),
    )
    .min(1, "Une plage horaire est requise"),
  streetName: z.string().min(1, "Le nom de la rue est requis"),
  streetNumberStart: z.string().optional(),
  streetNumberEnd: z.string().optional(),
  selectedDecree: z.object({ value: z.string(), label: z.string() }, { message: "L'arrêté est requis" }),
  description: z.string().min(1, "La description est requise"),
  width: z.number().min(1, "La largeur doit être d'au moins 1"),
});

type RestrictionFormValues = z.infer<typeof restrictionSchema>;

export const RestrictionMultiplePrompt = ({ saveRestriction, prefill, cancel }: RestrictionModalProps) => {
  const firstDecree = prefill.decrees?.[0];
  const selectedDecree = firstDecree ? { value: firstDecree.id, label: firstDecree.name } : undefined;
  const form = useForm<RestrictionFormValues>({
    resolver: zodResolver(restrictionSchema),
    mode: "all",
    defaultValues: {
      timeSpans:
        prefill.timeSpans?.map((item) => ({
          ...item,
          startDate: item.startDate ? new Date(item.startDate) : new Date(),
          endDate: item.endDate ? new Date(item.endDate) : new Date(),
        })) ?? [],
      streetName: prefill.streetName ?? undefined,
      description: prefill.description ?? undefined,
      width: prefill?.width ?? 2,
      streetNumberStart: prefill.streetNumberStart ?? undefined,
      streetNumberEnd: prefill.streetNumberEnd ?? undefined,
      selectedDecree,
    },
  });

  const { setValue, control } = form;

  useEffect(() => {
    (async () => {
      if (!prefill?.streetName) {
        const newLineParking = await getPlace(prefill.editLine);
        setValue("streetName", newLineParking.properties.streetName ?? "");
        setValue("streetNumberStart", newLineParking.properties.streetNumberStart ?? undefined);
        setValue("streetNumberEnd", newLineParking.properties.streetNumberEnd ?? undefined);
      }
    })();
  }, [prefill, setValue]);

  const isEdit = !isNil(prefill.id);

  const dismiss = () => cancel(isEdit);
  const save = (promptedProperties: RestrictionFormValues) => {
    saveRestriction({
      ...prefill,
      ...promptedProperties,
      decreeIds: [promptedProperties.selectedDecree.value],
      timeSpans: promptedProperties.timeSpans.map((span) => ({
        ...span,
        startDate: span.startDate.toISOString(),
        endDate: span.endDate.toISOString(),
      })),
    });
  };

  const headerText = useMemo(() => {
    return isEdit ? "Modifier plusieurs restrictions" : "Créer plusieurs restrictions";
  }, [isEdit]);

  return (
    <Dialog open>
      <DialogContent className="w-128">
        <DialogHeader>
          <DialogTitle>{headerText}</DialogTitle>
          <DialogDescription>Informations sur la restriction</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(save)} className="space-y-6">
            <FormField
              control={control}
              name="timeSpans"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Plages horaires <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <TimeSpanListing
                      timeSpans={value}
                      setTimeSpans={onChange}
                      options={{ showSelectDate: true, showTimeOfDay: true }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="streetName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom de la rue <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DecreeSingleSelect categories={[DecreeCategory.Temporary]} required />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="width"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Largeur <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <InputNumber {...field} min={1} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={dismiss} variant="secondary" type="button">
                Annuler
              </Button>
              <Button>Enregistrer</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
